/**
 * SAI (Scroll Animation Interface)
 * Intersection Observer を使用したスクロールアニメーション
 *
 * @example
 * // JavaScript
 * import SAI from './lib/sai.js';
 * new SAI();
 *
 * // HTML（プリセットモード）
 * <div data-sai="fade-up">コンテンツ</div>
 * <div data-sai="fade-left" data-sai-delay="200">遅延付き</div>
 *
 * // HTML（CSS 駆動モード: unique）
 * //   JS は交差時に `.sai-animate` を付与/除去するだけ。
 * //   初期スタイル・transition・終端スタイルは CSS 側で自由に定義する。
 * <div data-sai="unique" class="myReveal">CSS で制御</div>
 *
 * // CSS
 * // .myReveal {
 * //   opacity: 0;
 * //   transform: translateY(40px);
 * //   transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
 * //               transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
 * // }
 * // [data-sai="unique"].sai-animate.myReveal {
 * //   opacity: 1;
 * //   transform: translateY(0);
 * // }
 *
 * // 注意:
 * // - unique モードでは `data-sai-delay` / `data-sai-duration` / `data-sai-easing` は
 * //   無視されるため、CSS の transition-delay / duration / timing-function で表現する。
 * // - `data-sai-once="false"` による画面外リセットは `.sai-animate` 付け外しで引き続き有効。
 */

const defaultOptions = {
  offset: 100,
  delay: 0,
  duration: 800,
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  once: true,
  threshold: 0.1,
  debug: false
};

const predefinedAnimations = {
  'fade-in': {
    initial: { opacity: '0' },
    animate: { opacity: '1' }
  },
  'fade-up': {
    initial: { opacity: '0', transform: 'translate3d(0, 24px, 0)' },
    animate: { opacity: '1', transform: 'translate3d(0, 0, 0)' }
  },
  'fade-down': {
    initial: { opacity: '0', transform: 'translate3d(0, -24px, 0)' },
    animate: { opacity: '1', transform: 'translate3d(0, 0, 0)' }
  },
  'fade-left': {
    initial: { opacity: '0', transform: 'translate3d(24px, 0, 0)' },
    animate: { opacity: '1', transform: 'translate3d(0, 0, 0)' }
  },
  'fade-right': {
    initial: { opacity: '0', transform: 'translate3d(-24px, 0, 0)' },
    animate: { opacity: '1', transform: 'translate3d(0, 0, 0)' }
  },
  'zoom-in': {
    initial: { opacity: '0', transform: 'scale(0.96)' },
    animate: { opacity: '1', transform: 'scale(1)' }
  },
  // CSS 駆動モード: JS はクラス付与/除去のみ、transition と styles は CSS 側で定義
  unique: { mode: 'class' }
};

export default class SAI {
  static instance = null;
  observer = null;
  elements = new WeakMap();
  isInitialized = false;
  options = defaultOptions;
  resizeHandler = null;

  constructor(options = {}) {
    if (SAI.instance) {
      if (options.debug) {
        console.warn('[SAI] Already initialized.');
      }
      return SAI.instance;
    }

    SAI.instance = this;
    this.init(options);
  }

  log(level, message, ...args) {
    if (level === 'error') {
      console.error(`[SAI] ${message}`, ...args);
    } else if (this.options.debug) {
      console[level](`[SAI] ${message}`, ...args);
    }
  }

  getElementOption(element, option, defaultValue) {
    const datasetKey = `sai${option.charAt(0).toUpperCase() + option.slice(1)}`;
    const value = element.dataset[datasetKey];

    if (value === undefined) return defaultValue;

    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed < 0) {
      this.log('warn', `Invalid value for ${datasetKey}: "${value}"`, element);
      return defaultValue;
    }

    return parsed;
  }

  applyInitialStyles(element, animation) {
    // unique モードは CSS 側で初期スタイル・transition を定義する前提のため、JS では何もしない
    if (animation.mode === 'class') return;

    const { initial, animate } = animation;

    Object.entries(initial).forEach(([prop, value]) => {
      element.style[prop] = value;
    });

    const duration = this.getElementOption(
      element,
      'duration',
      this.options.duration
    );
    const delay = this.getElementOption(element, 'delay', this.options.delay);
    const easing = element.dataset.saiEasing || this.options.easing;

    element.style.transitionProperty = Object.keys(animate).join(', ');
    element.style.transitionDuration = `${duration}ms`;
    element.style.transitionDelay = `${delay}ms`;
    element.style.transitionTimingFunction = easing;
  }

  isElementInOrAboveViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.bottom <= window.innerHeight + this.options.offset;
  }

  animate(element, immediate = false) {
    const elementInfo = this.elements.get(element);
    if (!elementInfo || elementInfo.animated) return;

    const { animation } = elementInfo;

    if (animation.mode === 'class') {
      element.classList.add('sai-animate');
      elementInfo.animated = true;
      return;
    }

    if (immediate) {
      element.style.transitionDuration = '0ms';
      Object.entries(animation.animate).forEach(([prop, value]) => {
        element.style[prop] = value;
      });
      element.classList.add('sai-animate');
      elementInfo.animated = true;
      return;
    }

    requestAnimationFrame(() => {
      Object.entries(animation.animate).forEach(([prop, value]) => {
        element.style[prop] = value;
      });
      element.classList.add('sai-animate');
      elementInfo.animated = true;
    });
  }

  reset(element) {
    const elementInfo = this.elements.get(element);
    if (!elementInfo) return;

    if (elementInfo.animation.mode === 'class') {
      element.classList.remove('sai-animate');
      elementInfo.animated = false;
      return;
    }

    this.applyInitialStyles(element, elementInfo.animation);
    element.classList.remove('sai-animate');
    elementInfo.animated = false;
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      const element = entry.target;
      const elementInfo = this.elements.get(element);
      if (!elementInfo) return;

      if (entry.isIntersecting) {
        this.animate(element);
        if (elementInfo.once) {
          this.observer?.unobserve(element);
        }
      } else if (!elementInfo.once && elementInfo.animated) {
        this.reset(element);
      }
    });
  }

  init(customOptions = {}) {
    if (this.isInitialized) return;

    this.options = { ...defaultOptions, ...customOptions };

    const targets = document.querySelectorAll('[data-sai]');
    if (targets.length === 0) return;

    this.observer = new IntersectionObserver(
      entries => this.handleIntersection(entries),
      {
        rootMargin: `${-this.options.offset}px 0px`,
        threshold: this.options.threshold
      }
    );

    targets.forEach(element => {
      const animationType = element.dataset.sai || 'fade-up';
      const animation = predefinedAnimations[animationType];

      if (!animation) {
        this.log('warn', `Unknown animation type: ${animationType}`, element);
        return;
      }

      const once = element.dataset.saiOnce !== 'false';

      this.elements.set(element, { animation, once, animated: false });

      if (this.isElementInOrAboveViewport(element)) {
        this.animate(element, true);
        if (!once) {
          this.observer.observe(element);
        }
      } else {
        this.applyInitialStyles(element, animation);
        this.observer.observe(element);
      }
    });

    document.documentElement.classList.add('sai-init');
    this.isInitialized = true;

    this.resizeHandler = this.debounce(() => this.handleResize(), 150);
    window.addEventListener('resize', this.resizeHandler);
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  handleResize() {
    if (!this.observer) return;

    this.observer.disconnect();
    this.observer = new IntersectionObserver(
      entries => this.handleIntersection(entries),
      {
        rootMargin: `${-this.options.offset}px 0px`,
        threshold: this.options.threshold
      }
    );

    document.querySelectorAll('[data-sai]').forEach(element => {
      const elementInfo = this.elements.get(element);
      if (elementInfo && !elementInfo.animated) {
        this.observer.observe(element);
      }
    });
  }

  destroy() {
    this.observer?.disconnect();
    this.observer = null;

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    document.querySelectorAll('[data-sai]').forEach(element => {
      element.style.opacity = '';
      element.style.transform = '';
      element.style.transitionProperty = '';
      element.style.transitionDuration = '';
      element.style.transitionDelay = '';
      element.style.transitionTimingFunction = '';
      element.classList.remove('sai-animate');
    });

    document.documentElement.classList.remove('sai-init');
    this.elements = new WeakMap();
    this.isInitialized = false;
    SAI.instance = null;
  }

  refresh() {
    const currentOptions = this.options;
    this.destroy();
    this.init(currentOptions);
  }
}
